import { baseApi as api } from './baseApi';
import type { Survey, SurveyQuestion } from './platformApi';

export interface BlogPost {
  _id:             string;
  title:           string;
  excerpt:         string;
  content:         string;
  imageUrl?:       string;
  authorId:        string;
  authorSnapshot:  { name: string; lastName: string };
  officeId?:       string | null;
  officeSnapshot?: { name: string } | null;
  audience?:       BlogAudience;
  surveyId?:       string | null;
  isPublished:     boolean;
  /** Abierta al público: se ve sin login en el sitio. Independiente de isPublished. */
  isPublic?:       boolean;
  publishedAt?:    string;
  /** Contadores que vienen del listado (no del detalle). */
  commentsCount?:  number;
  likesCount?:     number;
  /** Si el usuario actual ya le dio like. */
  likedByMe?:      boolean;
  /** Novedad programada: se publica sola al llegar esta fecha/hora (solo en borradores). */
  scheduledAt?:    string | null;
  /**
   * Solo en el panel de gestión: si ESTE usuario puede editar ESTA novedad. El permiso
   * es por post, no por rol — el office_admin ve las generales pero solo toca las de su
   * oficina. Lo resuelve el backend, que es el que después acepta o rechaza.
   */
  puedeEditar?:    boolean;
  createdAt:       string;
  updatedAt:       string;
  // Solo en getBlogPost (detalle): la encuesta embebida + si el usuario ya respondió.
  survey?:         Survey | null;
  answered?:       boolean;
}

export type BlogAudience = 'all' | 'agents' | 'office_admins';

/** Sección del listado: de una oficina (`officeId` puesto) o corporativas (sin oficina). */
export type SeccionNovedades = 'oficina' | 'corporativa';

export interface CreateBlogPostDTO {
  title:       string;
  excerpt:     string;
  content:     string;
  imageUrl?:   string;
  officeId?:   string | null;
  /** A quién le llega. El admin de oficina no elige: siempre sus agentes. */
  audience?:   BlogAudience;
  isPublished: boolean;
  /** Abrirla al público (visible sin login en el sitio). */
  isPublic?:   boolean;
  /** ISO de publicación programada; null/'' quita la programación. Se ignora si isPublished. */
  scheduledAt?: string | null;
  /** Encuesta embebida (opcional): preguntas de la Survey asociada. */
  questions?:  SurveyQuestion[];
  /**
   * A qué inmobiliarias va. Solo lo respeta el backend si quien escribe es staff de
   * plataforma; el resto siempre publica para la suya.
   *  · `mi_tenant` (default) · `todos` (toda la red) · `algunos` (las de `tenantIds`)
   */
  alcance?:    'mi_tenant' | 'todos' | 'algunos';
  tenantIds?:  string[];
}

/** null/[] en `questions` quita la encuesta (si no tiene respuestas). */
export type UpdateBlogPostDTO = Omit<Partial<CreateBlogPostDTO>, 'questions'> & {
  questions?: SurveyQuestion[] | null;
};

export interface CommentReaction {
  emoji:   string;
  userIds: string[];
}

export interface BlogComment {
  _id:            string;
  postId:         string;
  parentId:       string | null;
  authorId:       string;
  authorSnapshot: { name: string; lastName: string; avatar?: string };
  content:        string;
  isDeleted:      boolean;
  reactions:      CommentReaction[];
  editedAt:       string | null;
  createdAt:      string;
  replies?:       BlogComment[];
}


/** Quién leyó una novedad. Sale de analytics_events (page_view), no del localStorage. */
export interface LectorNovedad {
  userId: string; name: string; avatar?: string | null;
  primeraVez?: string; veces?: number;
  /** Oficina del agente — el desglose es lo que dice a quién reclamarle. */
  office?: string;
  /** Llegó por la burbuja de Gustavo (el link trae `?ref=gustavo`). */
  porGustavo?: boolean;
}
/** Cobertura de una oficina: cuántos de los suyos la abrieron. */
export interface CoberturaOficina { office: string; total: number; leyeron: number; pct: number; }
export interface LectoresNovedad {
  postId: string; titulo: string; publicada: string | null;
  total: number; pct: number;
  /** A quién iba dirigida: define de quiénes es el `total`. */
  audience?: BlogAudience;
  leyeron: LectorNovedad[];
  faltan: LectorNovedad[];
  porOficina?: CoberturaOficina[];
  ritmo?: {
    /** Horas MEDIANAS hasta abrirla desde que se publicó (mediana, no promedio). */
    medianaHs: number | null;
    enPrimeras24h: number;
    repitieron: number;
    /** Cuántos entraron por la burbuja de Gustavo. Es la medición de si el empujón sirvió. */
    porGustavo?: number;
  };
}

/** La última novedad que este usuario no abrió. `null` si está al día. */
export interface NovedadPendiente {
  post: {
    _id: string;
    title: string;
    excerpt: string;
    imageUrl: string | null;
    publishedAt: string | null;
  } | null;
}

export const blogApi = api.injectEndpoints({
  endpoints: (build) => ({
    getLectoresNovedad: build.query<LectoresNovedad, string>({
      query: (id) => `/blog/${id}/lectores`,
      transformResponse: (res: { data: LectoresNovedad }) => res.data,
    }),

    /** Like/unlike de una novedad. Devuelve el conteo fresco y si quedó dado. */
    toggleBlogLike: build.mutation<{ liked: boolean; likesCount: number }, string>({
      query: (id) => ({ url: `/blog/${id}/like`, method: 'POST' }),
      transformResponse: (res: { data: { liked: boolean; likesCount: number } }) => res.data,
      invalidatesTags: ['BlogPost'],
    }),


    getBlogPosts: build.query<{ posts: BlogPost[]; total: number }, { limit?: number; offset?: number; seccion?: SeccionNovedades; q?: string }>({
      query: ({ limit = 20, offset = 0, seccion, q } = {}) =>
        `/blog?limit=${limit}&offset=${offset}${seccion ? `&seccion=${seccion}` : ''}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      transformResponse: (r: any) => r.data,
      providesTags: ['BlogPost'],
    }),

    /** Lo que Gustavo ofrece en su burbuja. Sin `providesTags`: se refresca sola con el
     *  poll del asistente, y el backend ya la cachea 60 s. */
    getNovedadPendiente: build.query<NovedadPendiente, void>({
      query: () => '/blog/pendiente',
      transformResponse: (r: { data: NovedadPendiente }) => r.data,
    }),

    /**
     * "Ya las vi todas". Corre el corte hasta ahora: lo anterior deja de contar como
     * pendiente aunque no se haya abierto. Invalida `BlogPost` para que el contador y la
     * campanita bajen en el momento.
     */
    marcarNovedadesLeidas: build.mutation<{ marcadas: number }, void>({
      query: () => ({ url: '/blog/marcar-leidas', method: 'POST' }),
      transformResponse: (r: { data: { marcadas: number } }) => r.data,
      invalidatesTags: ['BlogPost'],
    }),

    getRecentBlogPosts: build.query<BlogPost[], void>({
      query: () => '/blog/recent',
      transformResponse: (r: any) => r.data,
      providesTags: ['BlogPost'],
    }),

    getManagedBlogPosts: build.query<{ posts: BlogPost[]; total: number }, { limit?: number; offset?: number; seccion?: SeccionNovedades; q?: string } | void>({
      query: (params) => {
        const limit  = params?.limit  ?? 50;
        const offset = params?.offset ?? 0;
        const seccion = params?.seccion ? `&seccion=${params.seccion}` : '';
        const q = params?.q ? `&q=${encodeURIComponent(params.q)}` : '';
        return `/blog/manage?limit=${limit}&offset=${offset}${seccion}${q}`;
      },
      transformResponse: (r: any) => r.data,
      providesTags: [{ type: 'BlogPost', id: 'MANAGE' }],
    }),

    getBlogPost: build.query<BlogPost, string>({
      query: (id) => `/blog/${id}`,
      transformResponse: (r: any) => r.data,
      providesTags: (_r, _e, id) => [{ type: 'BlogPost', id }],
    }),

    createBlogPost: build.mutation<BlogPost, CreateBlogPostDTO>({
      query: (body) => ({ url: '/blog', method: 'POST', body }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: ['BlogPost'],
    }),

    updateBlogPost: build.mutation<BlogPost, { id: string; body: UpdateBlogPostDTO }>({
      query: ({ id, body }) => ({ url: `/blog/${id}`, method: 'PUT', body }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: (_r, _e, { id }) => ['BlogPost', { type: 'BlogPost', id }],
    }),

    deleteBlogPost: build.mutation<void, string>({
      query: (id) => ({ url: `/blog/${id}`, method: 'DELETE' }),
      invalidatesTags: ['BlogPost'],
    }),

    // ─── Comentarios ──────────────────────────────────────────────────────────
    getBlogComments: build.query<BlogComment[], string>({
      query: (postId) => `/blog/${postId}/comments`,
      transformResponse: (r: any) => r.data,
      providesTags: (_r, _e, postId) => [{ type: 'BlogComment', id: postId }],
    }),

    addBlogComment: build.mutation<BlogComment, { postId: string; content: string; parentId?: string | null }>({
      query: ({ postId, ...body }) => ({ url: `/blog/${postId}/comments`, method: 'POST', body }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: (_r, _e, { postId }) => [{ type: 'BlogComment', id: postId }],
    }),

    editBlogComment: build.mutation<BlogComment, { commentId: string; postId: string; content: string }>({
      query: ({ commentId, content }) => ({ url: `/blog/comments/${commentId}`, method: 'PATCH', body: { content } }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: (_r, _e, { postId }) => [{ type: 'BlogComment', id: postId }],
    }),

    deleteBlogComment: build.mutation<void, { commentId: string; postId: string }>({
      query: ({ commentId }) => ({ url: `/blog/comments/${commentId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { postId }) => [{ type: 'BlogComment', id: postId }],
    }),

    reactBlogComment: build.mutation<BlogComment, { commentId: string; postId: string; emoji: string }>({
      query: ({ commentId, emoji }) => ({ url: `/blog/comments/${commentId}/react`, method: 'POST', body: { emoji } }),
      transformResponse: (r: any) => r.data,
      invalidatesTags: (_r, _e, { postId }) => [{ type: 'BlogComment', id: postId }],
    }),
  }),
});

export const {
  useToggleBlogLikeMutation,
  useGetBlogPostsQuery,
  useGetRecentBlogPostsQuery,
  useGetManagedBlogPostsQuery,
  useGetBlogPostQuery,
  useCreateBlogPostMutation,
  useUpdateBlogPostMutation,
  useDeleteBlogPostMutation,
  useGetBlogCommentsQuery,
  useAddBlogCommentMutation,
  useEditBlogCommentMutation,
  useDeleteBlogCommentMutation,
  useReactBlogCommentMutation,
  useGetLectoresNovedadQuery,
  useGetNovedadPendienteQuery,
  useMarcarNovedadesLeidasMutation,
} = blogApi;
